<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { page } from '$app/stores';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { htmlEmbedFirstInteraction } from '$lib/global/tracking/v4';
    import { resolvePendingEmbed } from '$lib/Stores/blockReveal';

    import {
        receiveIframeDimensionsUpdates,
        sendSetBodyContentMessage,
        sendSetHeadContentMessage,
    } from '@perspective-software/cross-origin-html-embed';
    import unescape from 'lodash/unescape';
    import { onDestroy } from 'svelte';

    import {
        AI_EMBED_LOADING_MIN_HEIGHT,
        EMBED_HEIGHT_POLL_INTERVAL_MS,
        EMBED_HEIGHT_POLL_TIMEOUT_MS,
        NORMALIZE_CSS,
    } from '../constants';
    import { createEmbedHeightTracker } from '../embedHeightTracker';
    import {
        createUniqueCrossOriginIframeSandboxUrl,
        getEmbedHeightCacheKey,
        readCachedEmbedHeight,
        writeCachedEmbedHeight,
    } from '../helpers';

    // Please also check out "Notes.md" for more details on "first interaction detection".

    export let head = '';
    export let body = '';
    export let enforceCenteredCustomHtmlContent = '';
    export let id = '';
    export let campaignId = '';
    export let firstResultPage = '';
    export let nextPage = '';
    export let provider = '';
    export let layout = DEFAULT_LAYOUT;

    const {
        data: { pageIdSlugMap },
    } = $page;

    const nextPageNavigator = new NextPageNavigator(campaignId, firstResultPage, pageIdSlugMap);

    const iframeUrl = createUniqueCrossOriginIframeSandboxUrl(`${campaignId}-${id}`);

    // FUN-3908: The sandbox iframe mounts with height 0 and only allocates its
    // real height once the cross-origin sandbox has loaded and reported its
    // content dimensions. Start with the height measured on a previous visit
    // (if any), so the layout is correct immediately.
    const heightCacheKey = getEmbedHeightCacheKey(campaignId, id, window.innerWidth);
    const cachedHeight = readCachedEmbedHeight(heightCacheKey);

    let iframeHeight = cachedHeight ?? 0;

    // CVT-4096: Whether the sandbox has finished measuring the embed in this
    // page load, i.e. its reported height has stopped changing.
    let hasMeasurementSettled = false;

    // CVT-4096: Deciding when a reported height is the embed's own and final is
    // subtle enough to live in its own unit-tested module.
    const heightTracker = createEmbedHeightTracker({
        cachedHeight,
        onChange: (state) => {
            iframeHeight = state.height;
            hasMeasurementSettled = state.hasMeasurementSettled;
        },
        onSettle: (height) => writeCachedEmbedHeight(heightCacheKey, height),
    });

    // Until the real height is known, standalone AI embeds reserve most of the
    // viewport so blocks below them (e.g. a CTA button) don't flash near the
    // top of the page and then jump down. Scoped to AI blocks outside layout
    // columns: AI widgets inside a column would break the layout with such a
    // placeholder, and classic custom HTML embeds may be script-only.
    //
    // CVT-4096: A cached height was measured on a previous visit and is correct
    // from the first frame, so those embeds never need the placeholder at all.
    //
    // CAP-4418: This only ever covered the page-level case, and reserving a
    // guessed height is a poor fix in general — collapsing the placeholder down
    // to the embed's real height shifts the page as far as growing from 0 did.
    // The hold below is what keeps the page still; the placeholder stays only as
    // the behaviour a page falls back to once that hold times out.
    $: reserveLoadingMinHeight =
        provider === 'ai' && !layout.isInColumn && cachedHeight === null && !hasMeasurementSettled;

    // CAP-4418: The page seeds every AI embed as pending from its own data, so
    // the blocks after this one are held until its height is known and can be
    // painted once, in their final position. A height cached on an earlier visit
    // is already correct on the first frame, so release the hold straight away.
    if (cachedHeight !== null) {
        resolvePendingEmbed(id);
    }

    $: if (hasMeasurementSettled) {
        resolvePendingEmbed(id);
    }

    // A block that is torn down mid-measurement must not hold the page back.
    onDestroy(() => resolvePendingEmbed(id));

    let unsubscribeReceiveDimensionsUpdates: (() => void) | null = null;
    let unsubscribeFirstInteractionListener: (() => void) | null = null;
    let unsubscribePageNavigationActionsListener: (() => void) | null = null;
    let unsubscribeContentHeightListener: (() => void) | null = null;

    onDestroy(() => {
        unsubscribeFirstInteractionListener?.();
        unsubscribePageNavigationActionsListener?.();
        unsubscribeContentHeightListener?.();
        unsubscribeReceiveDimensionsUpdates?.();

        heightTracker.destroy();
    });

    const headHtmlToInject = `
            ${NORMALIZE_CSS}
            <style>
                body {
                    width: 100%;
                    ${
                        enforceCenteredCustomHtmlContent
                            ? 'display: flex; flex-direction: column; justify-content: center; align-items: center;'
                            : ''
                    }
                }
                style, script, title {
                    display: none !important;
                }
                iframe {
                    max-width: 100% !important;
                }
            </style>
            ${unescape(head)}
    `;

    const bodyHtmlToInject = `
        <script>
            (function() {
                function navigateToNextPage() {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "navigate-to-next-page"
                    }, "${window.location.origin}");
                }

                function navigateToResultPage() {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "navigate-to-result-page"
                    }, "${window.location.origin}");
                }

                function navigateToPageBySlug(pageSlug) {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "navigate-to-page-by-slug",
                        pageSlug: pageSlug
                    }, "${window.location.origin}");
                }

                window.perspective = {
                    navigateToNextPage: navigateToNextPage,
                    navigateToResultPage: navigateToResultPage,
                    navigateToPageBySlug: navigateToPageBySlug,
                };

                // CVT-4096: The host cannot tell a sandbox that has not injected
                // its content yet from one whose content is genuinely only a few
                // px tall (an AI divider settles at ~8px, the empty sandbox
                // document reports ~8-16px), so the sandbox reports its content
                // height explicitly once the content is in the DOM.
                //
                // This script is injected ahead of the embed content, and the
                // whole body is appended in one synchronous task, so a timeout
                // scheduled here runs once the content is present. It must be a
                // timeout rather than a frame callback: these iframes routinely
                // sit far below the fold (not least because of the loading
                // placeholder itself), and browsers do not run frame callbacks
                // for offscreen cross-origin frames.
                //
                // The height is measured and sent here rather than left to the
                // sandbox's ResizeObserver, so the host gets one authoritative
                // value instead of having to guess whether a report describes
                // the content or the empty document that preceded it. Reading
                // the rect forces layout, so the value is never stale.
                //
                // CAP-4418: That first measurement describes the content before
                // any of its external resources have applied — a Tailwind CDN
                // build, webfonts, images — so it is routinely a fraction of the
                // real height, or a fraction of a pixel. The sandbox's own
                // ResizeObserver is supposed to correct it, but its callbacks
                // are delivered in the rendering steps, which browsers skip for
                // a cross-origin iframe that is entirely offscreen. So we keep
                // re-measuring on a timer and report every change ourselves.
                function measureContentHeight() {
                    return document.documentElement.getBoundingClientRect().height;
                }

                setTimeout(function() {
                    var lastReportedHeight = measureContentHeight();

                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "content-injected",
                        contentHeight: lastReportedHeight
                    }, "${window.location.origin}");

                    var elapsedMs = 0;

                    // Runs to the timeout rather than stopping once the height
                    // has held steady for a while: for an offscreen embed this
                    // timer is the only channel that still works, so an embed
                    // that goes quiet and then changes again — a slow image, a
                    // webfont, a third-party widget finishing — would keep a
                    // height it has outgrown. Measuring costs a rect read on a
                    // tiny document, and nothing is posted unless it changed.
                    var pollIntervalId = setInterval(function() {
                        elapsedMs += ${EMBED_HEIGHT_POLL_INTERVAL_MS};

                        var height = measureContentHeight();

                        if ( height !== lastReportedHeight ) {
                            lastReportedHeight = height;

                            // Reported as a separate type from "content-injected":
                            // that message doubles as the signal that the content
                            // is in the DOM at all, and must stay a one-off.
                            window.parent.postMessage({
                                _isSandboxMessage: true,
                                type: "content-resized",
                                contentHeight: height
                            }, "${window.location.origin}");
                        }

                        if ( elapsedMs >= ${EMBED_HEIGHT_POLL_TIMEOUT_MS} ) {
                            clearInterval(pollIntervalId);
                        }
                    }, ${EMBED_HEIGHT_POLL_INTERVAL_MS});
                }, 0);
            })();
        ${'<'}/script>

        <script>
            (function() {
                const initiallyActiveElement = document.activeElement;
                let activeElementCheckInterval = undefined;
                let removeClickListener = null;

                function firstInteractionDetected() {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "first-interaction-detected"
                    }, "${window.location.origin}");

                    clearTimeout(activeElementCheckInterval);

                    if ( removeClickListener ) {
                        removeClickListener();
                    }
                }

                activeElementCheckInterval = setInterval(() => {
                    const activeElement = document.activeElement;

                    if ( activeElement !== initiallyActiveElement && activeElement !== document.body && activeElement.tagName === "IFRAME" ) {
                        firstInteractionDetected();
                    }
                }, 333);

                const clickListener = (e) => {
                    if ( e.target !== initiallyActiveElement && e.target !== document.body ) {
                        firstInteractionDetected();
                    }
                };

                document.addEventListener("click", clickListener, true);
                removeClickListener = () => {
                    document.removeEventListener("click", clickListener, true);
                }
            })();
        ${'<'}/script>

        ${unescape(body)}
    `;

    // We need to listen for message sent via postMessage
    // to track a first interaction that has been detected
    // the sandbox iframe.
    const listenForFirstInteraction = (iframe: HTMLIFrameElement) => {
        const messageListener = (event: MessageEvent) => {
            if (event.origin === iframeUrl.origin || event.source === iframe.contentWindow) {
                const message = event.data;

                if (
                    message &&
                    message._isSandboxMessage === true &&
                    message.type === 'first-interaction-detected'
                ) {
                    htmlEmbedFirstInteraction({ embedType: 'sandboxed' });
                }
            }
        };

        window.addEventListener('message', messageListener);

        return () => {
            window.removeEventListener('message', messageListener);
        };
    };

    // We need to listen for message from the iframe to perform
    // page navigation actions triggered from the iframe embed code.
    const listenForPageNavigationActions = (iframe: HTMLIFrameElement) => {
        const messageListener = (event: MessageEvent) => {
            if (event.origin === iframeUrl.origin || event.source === iframe.contentWindow) {
                const message = event.data;

                if (!message || message._isSandboxMessage !== true) {
                    return;
                }

                if (message.type === 'navigate-to-next-page') {
                    nextPageNavigator.go(nextPage);
                } else if (message.type === 'navigate-to-result-page') {
                    nextPageNavigator.go('result');
                } else if (
                    message.type === 'navigate-to-page-by-slug' &&
                    typeof message.pageSlug === 'string'
                ) {
                    nextPageNavigator.go(message.pageSlug);
                }
            }
        };

        window.addEventListener('message', messageListener);

        return () => {
            window.removeEventListener('message', messageListener);
        };
    };

    // CVT-4096: We need to listen for the sandbox reporting that the embed
    // content has been injected into its DOM. Only from that point on does a
    // reported height describe the embed rather than the sandbox shell.
    //
    // CAP-4418: The sandbox keeps re-measuring after that first report, so the
    // height still arrives for embeds the sandbox's ResizeObserver cannot see
    // (anything offscreen). Those follow-ups are ordinary height reports.
    const listenForContentHeightReports = (iframe: HTMLIFrameElement) => {
        const messageListener = (event: MessageEvent) => {
            if (event.origin === iframeUrl.origin || event.source === iframe.contentWindow) {
                const message = event.data;

                if (!message || message._isSandboxMessage !== true) {
                    return;
                }

                if (message.type === 'content-injected') {
                    heightTracker.reportInjectedContentHeight(message.contentHeight);
                } else if (message.type === 'content-resized') {
                    heightTracker.reportSandboxHeight(message.contentHeight);
                }
            }
        };

        window.addEventListener('message', messageListener);

        return () => {
            window.removeEventListener('message', messageListener);
        };
    };

    const onIframeLoad = (evt: Event) => {
        const iframe = evt.target as HTMLIFrameElement;

        unsubscribeFirstInteractionListener = listenForFirstInteraction(iframe);
        unsubscribePageNavigationActionsListener = listenForPageNavigationActions(iframe);
        unsubscribeContentHeightListener = listenForContentHeightReports(iframe);

        unsubscribeReceiveDimensionsUpdates = receiveIframeDimensionsUpdates(iframe, ({ data }) => {
            heightTracker.reportSandboxHeight(data.documentElementHeight);
        });

        sendSetHeadContentMessage(iframe, headHtmlToInject);
        sendSetBodyContentMessage(iframe, bodyHtmlToInject);
    };

    // Taken from here: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Permissions-Policy
    const allowPermissions = [
        'accelerometer',
        'ambient-light-sensor',
        'autoplay',
        'battery',
        'bluetooth',
        'browsing-topics',
        'camera',
        'display-capture',
        'document-domain',
        'encrypted-media',
        'execution-while-not-rendered',
        'execution-while-out-of-viewport',
        'fullscreen',
        'gamepad',
        'geolocation',
        'gyroscope',
        'hid',
        'identity-credentials-get',
        'idle-detection',
        'local-fonts',
        'magnetometer',
        'microphone',
        'midi',
        'otp-credentials',
        'payment',
        'picture-in-picture',
        'publickey-credentials-create',
        'publickey-credentials-get',
        'screen-wake-lock',
        'serial',
        'speaker-selection',
        'storage-access',
        'usb',
        'web-share',
        'window-management',
        'xr-spatial-tracking',
    ]
        .map((entry) => entry + ' *')
        .join('; ');
</script>

<div class="fun-flex fun-justify-center">
    <iframe
        src={iframeUrl.href}
        class="fun-w-full"
        title="Custom HTML Embed"
        loading="eager"
        scrolling="no"
        style={`height: ${iframeHeight}px;${
            reserveLoadingMinHeight ? ` min-height: ${AI_EMBED_LOADING_MIN_HEIGHT};` : ''
        }`}
        on:load={onIframeLoad}
        allow={allowPermissions}
        allowfullscreen
    />
</div>
